import type { FirestoreTimestampLike } from '../types/backend';

/**
 * Normalize a Firestore-sourced timestamp to epoch millis.
 *
 * Values read through `onSnapshot` arrive as real `Timestamp` objects (the
 * callable contract's ISO-string rule covers only the *callable* wire format,
 * where the backend serializes them itself), and **every snapshot mints a new
 * instance** — so comparing raw values with `!==` reports "changed" on every
 * unrelated field write in the same document. Always compare millis.
 *
 * The string arm keeps us correct if the backend ever writes ISO-8601 instead,
 * and `null` covers both "absent" and "unparseable" so a malformed value
 * degrades to "no timestamp" rather than to `NaN` comparisons that are silently
 * false in both directions.
 */
export function timestampMillis(
  v: FirestoreTimestampLike | string | null | undefined,
): number | null {
  if (v == null) return null;
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof v.toMillis === 'function') {
    const ms = v.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}
